/// <reference path="../types/env.d.ts" />
import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  VITE_APP_TITLE: z.string().min(1),
  VITE_ENABLE_MOCK_API: z.string().transform((v) => v === 'true'),
  VITE_MSAL_CLIENT_ID: z.string().uuid(),
  VITE_MSAL_TENANT_ID: z.string().uuid(),
  VITE_API_SCOPE: z.string().min(1).optional(),
  VITE_AZURE_AD_AUTHORITY: z.string().url(),
  VITE_AZURE_AD_CLIENT_ID: z.string().uuid(),

  VITE_APPROVER_USER_GUIDE_URL: z.string().min(1).optional(),
  VITE_ADMIN_USER_GUIDE_URL: z.string().min(1).optional(),
  VITE_LOG_ISSUE_URL: z.string().min(1).optional(),
  VITE_ONETRUST_SDK_URL: z.string().min(1).optional(),
  VITE_ONETRUST_DOMAIN_SCRIPT: z.string().min(1).optional(),

  // Feature flags — default to enabled; set to 'false' to disable
  VITE_NLQ_CHAT_ENABLED: z.string().default('true').transform((v) => v !== 'false'),

});

type RuntimeConfig = Partial<Record<keyof z.input<typeof envSchema>, string>>;

function isUnresolvedToken(value: string): boolean {
  return /^__[^\s]+__$/.test(value);
}

function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined' || !(window.__Mable_CONFIG__)) {
    return {};
  }

  const filteredEntries = Object.entries((window.__Mable_CONFIG__)!).filter(([, value]) => {
    if (typeof value !== 'string') {
      return false; 
    }

    return !isUnresolvedToken(value);
  });

  return Object.fromEntries(filteredEntries) as RuntimeConfig;
}

export const env = envSchema.parse({
  ...import.meta.env,
  ...getRuntimeConfig(),
});

/** Feature flags derived from runtime / build-time config. */
export const featureFlags = {
  /** Mirror of FeatureFlags:NLQChatEnabled in appsettings — hides NLQ chat when false. */
  nlqChatEnabled: env.VITE_NLQ_CHAT_ENABLED,
} as const;

const SUMMIT_MEETING_BASE_URL = 'https://summit.deloitte.com/admin/meeting-request/';

export function getSummitMeetingUrl(meetingId: string): string {
  if (!meetingId) return '';
  // Encode to prevent injection; meetingId is expected to be an alphanumeric ID
  const safe = encodeURIComponent(meetingId);
  return `${SUMMIT_MEETING_BASE_URL}${safe}`;
}
